/**
 * WidgetItem - Sidebar item for a widget
 *
 * Displays a widget in the sidebar with its icon and name.
 * Supports multi-select with Cmd/Ctrl+click and Shift+click.
 * Uses the unified SidebarItem component for consistent behavior.
 */

import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { WidgetWithContext, getWidgetIcon, getWidgetTypeLabel } from "@/hooks/use-widgets";
import { getBackendWidgetType } from "@/models/widget-v2";
import { createWidgetDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

interface WidgetItemProps {
  widget: WidgetWithContext;
  isActive?: boolean;
  // Multi-select props
  /** Whether this item is selected (for multi-select) */
  isSelected?: boolean;
  /** Ordered list of all widget IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

const WidgetItem: React.FC<WidgetItemProps> = ({
  widget,
  isActive = false,
  isSelected = false,
  orderedIds = [],
  onMultiSelectContextMenu,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const widgetChartType = getBackendWidgetType(widget.widget);
  const widgetName = widget.widget.metadata?.name || widget.widget.name || getWidgetTypeLabel(widgetChartType);
  const widgetIcon = getWidgetIcon(widgetChartType);
  const widgetType = getWidgetTypeLabel(widgetChartType);

  // Create drag payload
  const dragPayload = createWidgetDragPayload(widget.id, widgetName, widget.dashboardId);

  const handleClick = useCallback(() => {
    // Navigate to widget in its dashboard context or widget gallery
    const route = widget.dashboardId 
      ? `/dashboards?id=${widget.dashboardId}&widget=${widget.id}`
      : `/widgets?id=${widget.id}`;
    navigateTo(route, { title: widgetName });
  }, [widget.id, widget.dashboardId, widgetName, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  return (
    <>
      <SidebarItem
        id={widget.id}
        name={widgetName}
        resourceType="widget"
        icon={<span className="text-base">{widgetIcon}</span>}
        subtitle={widgetType}
        isActive={isActive}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        dragPayload={dragPayload}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

WidgetItem.displayName = "WidgetItem";

export default WidgetItem;
